import React from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import { HeadingTwo } from '@marty-js/design/src/atoms/h2';
import { GenericItemDataSheet } from '@marty-js/api-sdk/types';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { TableSimple } from '@marty-js/design/src/molecules/table-simple';
import { DateDisplay } from './date/date';

const Widget = styled.div`
  margin: 45px 0 30px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 60px 0 45px;
    `,
  )}
`;

export type SoftwareSpecsFile = {
  id: string;
  variantId: string;
  variantName: string;
  version: string;
  path: string;
  size: string;
  updateAt: string;
  url: string;
  os: string;
  dataSheet?: GenericItemDataSheet;
};

type Props = {
  allFiles: SoftwareSpecsFile[];
  currentFileId: string;
  softwareStatistics: number;
  updateAt?: string;
};

interface Row {
  label: string;
  value: any;
  url?: string;
  postLabel?: string;
}

export const SoftwareSpecs = ({ allFiles, currentFileId, softwareStatistics, updateAt }: Props) => {
  const t = useTranslation();
  const currentFile = allFiles.find((file) => currentFileId === file.id);
  const osSoftwareAll = allFiles.flatMap((file) => {
    return file.dataSheet?.groups?.flatMap((group) => {
      return group.attributes.reduce((acc, spec) => {
        if (spec.shortSlug === 'operating-system') {
          acc.push(spec.value);
        }

        return acc;
      }, []);
    });
  });

  const osSoftware: Row[] = [
    {
      label: "Système d'exploitation",
      value: osSoftwareAll.join(', '),
    },
  ];

  let specArray: Row[] = [];

  if (currentFile.dataSheet?.groups) {
    specArray = currentFile.dataSheet.groups.flatMap((group) => {
      return group.attributes.reduce((acc, spec) => {
        if (spec.shortSlug !== 'operating-system') {
          acc.push({
            label: spec.name,
            url: spec.url,
            value: spec.value,
          });
        }

        return acc;
      }, []);
    });
  }

  const rows: Row[] = [
    { label: t('design.version'), value: currentFile?.version },
    {
      label: t('design.lastUpdate'),
      value: <DateDisplay dateTime={updateAt} pattern={t('localeTimeSoftWare')} />,
    },
    { label: t('design.downloads'), value: softwareStatistics, postLabel: t('design.last7days') },
    ...specArray,
    ...osSoftware,
  ];

  return (
    <Widget data-testid="software-spec">
      <HeadingTwo>Spécifications</HeadingTwo>
      <TableSimple data={rows} />
    </Widget>
  );
};
